import React, { useMemo, useState } from 'react'
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'

import { DataTable } from '@/components/UI/DataTable'
import SkeletonRow from '@/components/UI/Skeleton'
import { ErrorPage } from '@/pages/Error'
import { IRecurringIncome } from '@/types'
import { EmptyContainer, EmptyMessage } from '../EmptyState'
import { recurringIncomeColumns, recurringIncomeState, RecurringIncomeState } from './columns'
import { useRecurringIncome } from './useRecurringIncome'

type StatusFilter = 'ALL' | RecurringIncomeState

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Ended', label: 'Ended' },
]

const matchesFilter = (row: IRecurringIncome, filter: StatusFilter) =>
  filter === 'ALL' || recurringIncomeState(row) === filter

// Primary income first, as it is the stream decisioning leans on, then newest first.
const byPrimaryThenNewest = (a: IRecurringIncome, b: IRecurringIncome) =>
  Number(b.isPrimaryIncome) - Number(a.isPrimaryIncome) ||
  new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()

export const RecurringIncome: React.FC = () => {
  const { data, isLoading, isError } = useRecurringIncome()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')

  const rows = useMemo(
    () => data.filter(row => matchesFilter(row, statusFilter)).sort(byPrimaryThenNewest),
    [data, statusFilter]
  )

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
        <Typography variant="h6">Recurring Income</Typography>
        {/* The icon sits on the dropdown's row and is centred on it; the label above is indented by
            the icon's width plus the gap, so it lines up with the dropdown's left edge. */}
        <Stack gap={0.5}>
          <Typography
            sx={{ fontSize: 14, fontWeight: 500, color: '#344054', lineHeight: '20px', pl: '28px' }}
          >
            By Status
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Filter">
              <FilterAltOutlinedIcon sx={{ fontSize: 20, color: '#344054' }} />
            </Tooltip>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={statusFilter}
                onChange={(event: SelectChangeEvent) =>
                  setStatusFilter(event.target.value as StatusFilter)
                }
                inputProps={{ 'aria-label': 'Filter by status' }}
                sx={{ borderRadius: '8px', fontSize: 14, color: '#344054' }}
              >
                {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Box>
      {isLoading ? (
        <SkeletonRow columns={recurringIncomeColumns} />
      ) : isError ? (
        <ErrorPage
          title="Unable to Fetch Recurring Income"
          message="We couldn't retrieve recurring income at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        />
      ) : rows.length > 0 ? (
        <DataTable<IRecurringIncome> rows={rows} columns={recurringIncomeColumns} />
      ) : (
        <EmptyContainer>
          <EmptyMessage>
            {statusFilter === 'ALL'
              ? 'No recurring income detected for this customer.'
              : 'No recurring income found for the selected status.'}
          </EmptyMessage>
        </EmptyContainer>
      )}
    </>
  )
}
